import { useEffect } from 'react';

/**
 * Gives one page its own browser tab — title and favicon — and puts the tab back on the way out.
 *
 * This is a single-page app, so `index.html` supplies one title and one icon for every route.
 * That is right for the intake app and wrong for a candidate: they open a link after accepting an
 * offer and get a tab reading "New Matte…" with the new-matter mark, which looks like something
 * forwarded to them by mistake. It is the same mismatch as the hostname, in the one piece of
 * chrome a form cannot style away.
 *
 * WHY THE ORIGINAL LINKS ARE REMOVED rather than just re-pointed. `index.html` declares five icon
 * links — an SVG plus .ico and PNG fallbacks for browsers that ignore SVG favicons. Overwriting
 * only the SVG leaves those fallbacks pointing at the intake mark, so the tab shows the right icon
 * in some browsers and the wrong one in others, which is worse than not trying. So every icon link
 * is taken out, one SVG link is added, and the originals are restored on unmount — the page is
 * client-routed, so leaving a stale icon behind would follow somebody around the rest of the app.
 */
export function usePageIdentity({ title, icon } = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    // Kept as nodes, not as markup, so they go back exactly as they were.
    const originals = [...document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')];
    let added = null;
    if (icon) {
      originals.forEach((link) => link.remove());
      added = document.createElement('link');
      added.setAttribute('rel', 'icon');
      added.setAttribute('type', 'image/svg+xml');
      added.setAttribute('href', icon);
      document.head.appendChild(added);
    }

    return () => {
      document.title = previousTitle;
      if (added) added.remove();
      originals.forEach((link) => document.head.appendChild(link));
    };
  }, [title, icon]);
}
